import { useCallback, useEffect, useRef, useState } from "react";

export interface UseRequestOptions<T> {
  /**
   * 初始数据。用于服务端预渲染注入的引导数据（如 window.__PRICING_PLANS__）：
   * 提供后，首帧即带数据渲染（loading=false），从而与预渲染 HTML 一致、可安全 hydrate；
   * 挂载后仍会在后台静默刷新一次以保证数据新鲜。
   */
  initialData?: T | null;
}

/**
 * 通用数据请求 hook：管理 loading / error / data 三态，并提供 reload。
 *
 * @param fetcher 返回 Promise 的请求函数
 * @param deps    依赖数组；其中任一变化会重新请求（默认 []，仅挂载时请求一次）
 * @param options 可选项；initialData 用于预渲染注入的引导数据
 *
 * 用法：
 *   useRequest(getDashboardSummary)               // 只请求一次
 *   useRequest(() => getApiTrends(days), [days])  // days 变化时重新请求
 *   useRequest(getPlans, [], { initialData })     // 带预渲染注入的初始数据
 */
export function useRequest<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList = [],
  options: UseRequestOptions<T> = {}
) {
  const { initialData = null } = options;
  const [data, setData] = useState<T | null>(initialData);
  // 有初始数据时首帧不进入 loading，避免闪烁与 hydration mismatch
  const [loading, setLoading] = useState(initialData == null);
  const [error, setError] = useState<string | null>(null);

  // 用 ref 持有最新 fetcher，避免把它塞进 effect 依赖
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // 持有最新 data，供后台刷新时判断是否需要展示 loading / error
  const dataRef = useRef<T | null>(data);
  dataRef.current = data;

  // 首次是否携带注入数据：若是，则首个请求走「后台静默刷新」，不切 loading
  const silentFirstRef = useRef(initialData != null);

  // reload 通过自增计数触发 effect 重跑
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (silentFirstRef.current) {
      // 带注入数据的首次请求：后台静默刷新，保持已有数据可见
      silentFirstRef.current = false;
    } else {
      setLoading(true);
    }
    setError(null);
    fetcherRef
      .current()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        // 已有数据时的后台刷新失败：保留旧数据，不覆盖为错误态
        if (!cancelled && dataRef.current == null) {
          setError(err instanceof Error ? err.message : "Request failed.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick, ...deps]);

  return { data, loading, error, reload };
}
